import { useState, useEffect } from "react";
import { useParams, Link as RouteLink, useNavigate } from "react-router";
import { useTranslation } from "react-i18next";
import {
  Breadcrumbs as MuiBreadcrumbs,
  Grid as MuiGrid,
  Typography as MuiTypography,
  TextField as MuiTextField,
  Tooltip as MuiTooltip,
  Link as MuiLink,
  Autocomplete as MuiAutocomplete,
} from "@mui/material";
import { NavigateNext as MuiNavigateNextIcon } from "@mui/icons-material";
import { Formik } from "formik";
import * as Yup from "yup";
import OrcidIdLogo from "app/assets/icons/orcid-id.svg?react";
/** Custom Components */
import { FormButton } from "app/shared/ui/Button/FormButton";
import { FacultyTrainingRoles } from "app/shared/constants";
/** Services */
import {
  getEditFacultyDetailsById,
  getEditFacultyAutocompleteOptions,
  getFacultyPrimaryDepartmentById,
  putFacultyDetailsByIdUpdate,
} from "app/services/facultyService";
import { useSelectedSubmission } from "app/services/selectedSubmissionService";
/** Context and Component to show the error on UI */
import { useAlerts, Theme } from "common";
import { sharedStyles } from "app/shared/ui/sharedStyles";

// ************ Edit Faculty Component ***********
const editFacultyInitialValues = {
  degrees: [],
  rank: {},
  departments: [],
};
const facultyPrimaryDepartmentValues = {
  values: [],
};
export const EditFaculty = (props) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  // **** Get Faculty Id ****
  const facultyId = useParams().facultyId;

  // **** Selected Submission Id ****
  const [getSelectedSubmission] = useSelectedSubmission().value;
  const selectedSubmission = getSelectedSubmission();

  const [commonsIdFieldError, setCommonsIdFieldError] = useState({
    value: null,
    isError: false,
  });

  // **** Initial Form State Values ****
  const [facultyEditFormValues, setFacultyEditFormValues] = useState(
    editFacultyInitialValues,
  );
  const [facultyPrimaryDepartment, setFacultyPrimaryDepartmentValues] =
    useState(facultyPrimaryDepartmentValues);
  // *** Fetch Faculty Details ***
  const [loading, setLoading] = useState(true);
  const [facultyDetailsFetchError, setFacultyDetailsFetchError] =
    useState(false);

  useEffect(() => {
    getEditFacultyDetailsById(
      setFacultyEditFormValues,
      setFacultyDetailsFetchError,
      setLoading,
      facultyId,
      selectedSubmission,
      editFacultyInitialValues,
      setAlert,
      clearAlert,
    );
    // eslint-disable-next-line
  }, [facultyId, selectedSubmission]);

  useEffect(() => {
    getFacultyPrimaryDepartmentById(
      setFacultyPrimaryDepartmentValues,
      facultyId,
      selectedSubmission,
      facultyPrimaryDepartmentValues,
      setAlert,
      clearAlert,
    );
    // eslint-disable-next-line
  }, [facultyId, selectedSubmission]);

  // **** Field Options ****
  const [rankOptionsList, setRankOptionsList] = useState([]);
  const [degreeOptionsList, setDegreeOptionsList] = useState([]);
  // **** Field Values ****
  const [rankInputValue, setRankInputValue] = useState("");
  const [degreeInputValue, setDegreeInputValue] = useState("");
  const { setAlert, clearAlert } = useAlerts();

  // **** Fetch Options for Autocomplete List ****
  const handleAutoCompleteInputChange = async (
    inputName,
    size,
    inputValue,
    setFieldInputValue,
    setFieldOptions,
  ) => {
    setFieldInputValue(inputValue);
    if (inputValue) {
      getEditFacultyAutocompleteOptions(
        inputName,
        size,
        inputValue,
        setFieldOptions,
        setAlert,
        clearAlert,
      );
    }
  };
  // **** Faculty Form Validation ****
  const facultyValidation = Yup.object().shape({
    orcidId: Yup.string().when("hasSourceOrcid", {
      is: (val) => val === false,
      then: () =>
        Yup.string().test((val) => {
          if (val) {
            if (val.length === 19) return true;
            if (val.length < 19 && val.length > 0) return false;
          } else {
            return true;
          }
        }),
    }),
    degrees: Yup.array().min(1).required("Required"),
    rank: Yup.object().required("Required"),
    department: Yup.object().required("Required"),
  });

  /** 
  redirect on cancel button click
  */
  const onCancelHandler = () => {
    navigate("/faculty");
  };

  const getOrcidIdTooltip = () => {
    return (
      <>
        The ORCID iD field is the individual’s persistent digital identifier{" "}
        <MuiLink
          href="https://orcid.org/"
          sx={{
            "&&": {
              fontWeight: "bold",
              color: Theme.palette.common.white,
              "&:visited": {
                color: Theme.palette.common.white,
              },
            },
          }}
          target="_blank"
        >
          (<u>https://orcid.org/</u>)
        </MuiLink>{" "}
        that can be used to retrieve their professional information —
        affiliations, grants, and publications. <br />
        <br />
        The source for this information is Stanford’s ORCID Integration
        application{" "}
        <MuiLink
          href="https://authorize.stanford.edu/orcid/OrcidApp"
          target="_blank"
          sx={{
            "&&": {
              fontWeight: "bold",
              color: Theme.palette.common.white,
              "&:visited": {
                color: Theme.palette.common.white,
              },
            },
          }}
        >
          (<u>https://authorize.stanford.edu/orcid/OrcidApp</u>)
        </MuiLink>
        . <br />
        <br />
        If the ORCID iD is missing for an individual, we would encourage that it
        be updated in the source system, which requires each individual to
        verify/create their own ORCID iD directly in the application.
      </>
    );
  };
  return (
    !loading &&
    !facultyDetailsFetchError &&
    facultyEditFormValues._links?.editDetails && (
      <MuiGrid
        container
        wrap="nowrap"
        direction="column"
        spacing={2}
        size={6}
        sx={{ justifyContent: "flex-start" }}
      >
        <MuiGrid>
          <MuiBreadcrumbs
            separator={<MuiNavigateNextIcon fontSize="small" />}
            aria-label="breadcrumb"
            sx={sharedStyles.breadcrumb}
          >
            <RouteLink to="/faculty">{t("faculty.title")}</RouteLink>
            <MuiTypography sx={{ color: "text.primary" }}>
              {t("faculty.edit.title")}
            </MuiTypography>
          </MuiBreadcrumbs>
        </MuiGrid>

        <MuiGrid size={4}>
          <Formik
            initialValues={facultyEditFormValues}
            enableReinitialize
            onSubmit={(values, { setSubmitting, setFieldError }) => {
              setSubmitting(false);

              putFacultyDetailsByIdUpdate(
                setCommonsIdFieldError,
                selectedSubmission,
                values,
                navigate,
                setAlert,
                clearAlert,
                t,
              );
            }}
            validationSchema={facultyValidation}
          >
            {(props) => {
              const {
                values,
                touched,
                errors,
                handleChange,
                handleBlur,
                handleSubmit,
                isValid,
                dirty,
              } = props;

              const handleChangeOrcID = (event) => {
                let { value } = event.target;

                // remove any non-alphanumeric characters except hyphens
                value = value.replace(/[^\d]/g, "");

                // insert hyphens after every 4 characters
                let formattedValue = "";
                for (let i = 0; i < value.length; i++) {
                  if (i > 0 && i % 4 === 0) {
                    formattedValue += "-";
                  }
                  formattedValue += value[i];
                }
                // update the state with formattedValue
                props.setFieldValue("orcidId", formattedValue);
              };

              return (
                <form onSubmit={handleSubmit} style={{ display: "flex" }}>
                  <MuiGrid
                    container
                    direction="column"
                    spacing={2}
                    sx={{ flex: "1 0 auto", justifyContent: "space-between" }}
                  >
                    <MuiGrid size={{ xs: 11 }}>
                      <MuiTypography
                        sx={{ color: "text.primary" }}
                        variant="h5"
                      >
                        {values.facultyFullName}
                      </MuiTypography>
                    </MuiGrid>
                    <MuiGrid size={{ xs: 12 }}>
                      <MuiGrid
                        container
                        direction="row"
                        spacing={1}
                        sx={{
                          justifyContent: "space-between",
                          alignItems: "flex-start",
                        }}
                      >
                        <MuiGrid size={{ xs: 11 }}>
                          <MuiTextField
                            id="orcidId"
                            name="orcidId"
                            label={`${t("faculty.edit.facultyList.orcidId")}`}
                            value={values.orcidId || ""}
                            onChange={handleChangeOrcID}
                            onBlur={handleBlur}
                            helperText={touched.orcidId ? errors.orcidId : ""}
                            error={touched.orcidId && Boolean(errors.orcidId)}
                            variant="outlined"
                            fullWidth
                            autoComplete="off"
                            disabled={values.hasSourceOrcid}
                            slotProps={{ htmlInput: { maxLength: 19 } }}
                          />
                        </MuiGrid>
                        <MuiGrid
                          size={{ xs: 1 }}
                          sx={{
                            "&&": {
                              paddingTop: "18px",
                            },
                          }}
                        >
                          <MuiTooltip
                            placement="right"
                            sx={{ fontSize: "14px", maxWidth: "500px" }}
                            title={getOrcidIdTooltip()}
                          >
                            <div style={{ height: "20px", width: "20px" }}>
                              <OrcidIdLogo />
                            </div>
                          </MuiTooltip>
                        </MuiGrid>
                      </MuiGrid>
                    </MuiGrid>
                    <MuiGrid size={{ xs: 11 }}>
                      <MuiTextField
                        id="commonsId"
                        name="commonsId"
                        label={`${t("faculty.edit.facultyList.commonsID")}`}
                        value={values.commonsId || ""}
                        onChange={(e) => {
                          const commonIdVal = e.target.value;
                          setCommonsIdFieldError({
                            ...commonsIdFieldError,
                            isError:
                              commonsIdFieldError.value &&
                              commonsIdFieldError.value === commonIdVal,
                          });

                          props.setFieldValue("commonsId", commonIdVal);
                        }}
                        onBlur={handleBlur}
                        variant="outlined"
                        error={commonsIdFieldError.isError}
                        helperText={
                          commonsIdFieldError.isError &&
                          `${t("faculty.edit.commonsIdError")}`
                        }
                        fullWidth
                        autoComplete="off"
                        slotProps={{ htmlInput: { maxLength: 20 } }}
                        onKeyPress={(e) => {
                          if (e.charCode === 32) {
                            e.preventDefault();
                          }
                        }}
                      />
                    </MuiGrid>
                    <MuiGrid size={{ xs: 11 }}>
                      <MuiTextField
                        id="email"
                        label={`${t("faculty.edit.facultyList.email")}`}
                        value={values.email}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        helperText={touched.email ? errors.email : ""}
                        error={touched.email && Boolean(errors.email)}
                        variant="outlined"
                        fullWidth
                        autoComplete="off"
                      />
                    </MuiGrid>
                    <MuiGrid size={{ xs: 11 }}>
                      <MuiAutocomplete
                        multiple
                        id="degrees"
                        name="degrees"
                        value={values.degrees}
                        options={degreeInputValue ? degreeOptionsList : []}
                        getOptionLabel={(option) => option.shortName ?? ""}
                        onChange={(e, value) => {
                          if (value) {
                            props.setFieldValue("degrees", value);
                          }
                        }}
                        getOptionDisabled={(option) =>
                          values.degrees &&
                          values.degrees.some((el) => {
                            return el.code === option.code;
                          })
                        }
                        renderInput={(params) => (
                          <MuiTextField
                            {...params}
                            label={`${t("faculty.edit.facultyList.degrees")}*`}
                            variant="outlined"
                            fullWidth
                            onChange={(e) => {
                              handleAutoCompleteInputChange(
                                "degree",
                                10,
                                e.target.value || "",
                                setDegreeInputValue,
                                setDegreeOptionsList,
                              );
                            }}
                          />
                        )}
                      />
                    </MuiGrid>
                    <MuiGrid size={{ xs: 11 }}>
                      <MuiAutocomplete
                        id="rank"
                        options={rankInputValue ? rankOptionsList : []}
                        getOptionLabel={(option) => option.name ?? ""}
                        value={values.rank}
                        clearOnEscape
                        onChange={(e, value) => {
                          props.setFieldValue("rank", value || "");
                        }}
                        renderInput={(params) => (
                          <MuiTextField
                            {...params}
                            name="rank"
                            label={`${t("faculty.edit.facultyList.rank")}*`}
                            variant="outlined"
                            fullWidth
                            onChange={(e) => {
                              handleAutoCompleteInputChange(
                                "rank",
                                10,
                                e.target.value || "",
                                setRankInputValue,
                                setRankOptionsList,
                              );
                            }}
                          />
                        )}
                      />
                    </MuiGrid>
                    <MuiGrid size={{ xs: 11 }}>
                      <MuiAutocomplete
                        options={
                          facultyPrimaryDepartment
                            ? facultyPrimaryDepartment.values
                            : []
                        }
                        getOptionLabel={(option) =>
                          option.name ? `${option.name} (${option.code})` : ""
                        }
                        value={values.department || []}
                        onChange={(e, value) => {
                          if (value) {
                            props.setFieldValue("department", value);
                          }
                        }}
                        renderInput={(params) => (
                          <MuiTextField
                            {...params}
                            label={`${t(
                              "faculty.edit.facultyList.primaryDepProg",
                            )}*`}
                            variant="outlined"
                            fullWidth
                          />
                        )}
                      />
                    </MuiGrid>
                    <MuiGrid size={{ xs: 11 }}>
                      <MuiTextField
                        id="researchInterest"
                        label={`${t(
                          "faculty.edit.facultyList.researchInterest",
                        )}`}
                        value={values.researchInterest}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        helperText={
                          touched.researchInterest
                            ? errors.researchInterest
                            : ""
                        }
                        error={
                          touched.researchInterest &&
                          Boolean(errors.researchInterest)
                        }
                        variant="outlined"
                        fullWidth
                        autoComplete="off"
                        slotProps={{ htmlInput: { maxLength: 200 } }}
                      />
                    </MuiGrid>
                    <MuiGrid size={{ xs: 11 }}>
                      <MuiAutocomplete
                        multiple
                        name="trainingRoles"
                        value={values.trainingRoles || []}
                        options={Object.keys(FacultyTrainingRoles) || []}
                        getOptionLabel={(option) =>
                          t(FacultyTrainingRoles[option])
                        }
                        onChange={(e, value) => {
                          if (value && value.length < 4) {
                            props.setFieldValue("trainingRoles", value);
                          }
                        }}
                        renderInput={(params) => (
                          <MuiTextField
                            {...params}
                            name="trainingRole"
                            label={`${t(
                              "faculty.edit.facultyList.trainingRole",
                            )}`}
                            variant="outlined"
                            fullWidth
                          />
                        )}
                      />
                    </MuiGrid>
                    <MuiGrid size={{ xs: 11 }}>
                      <MuiGrid
                        container
                        direction="row"
                        spacing={2}
                        sx={{
                          justifyContent: "flex-end",
                          alignItems: "center",
                        }}
                      >
                        <MuiGrid>
                          <FormButton
                            onClick={onCancelHandler}
                            name={t("globals.form.actionButtons.cancel")}
                            variant="outlined"
                          />
                        </MuiGrid>
                        <MuiGrid>
                          <FormButton
                            name={t("globals.form.actionButtons.save")}
                            variant="contained"
                            type="submit"
                            disabled={
                              !dirty ||
                              !isValid ||
                              !values.submissionName.trim() ||
                              commonsIdFieldError.isError
                            }
                          />
                        </MuiGrid>
                      </MuiGrid>
                    </MuiGrid>
                  </MuiGrid>
                </form>
              );
            }}
          </Formik>
        </MuiGrid>
      </MuiGrid>
    )
  );
};
